import type { ActualFileObject, FilePondErrorDescription, FilePondFile } from 'filepond';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import { ArrowRight } from 'lucide-react';
import { usePostHog } from 'posthog-js/react';
import { type FormEvent, type RefObject, useRef, useState } from 'react';
import { FilePond, type FilePondProps, registerPlugin } from 'react-filepond';
import { useTranslation } from 'react-i18next';
import filePondConfig from '@/../config/filepond.json';
import { useAppContext } from '@/app/providers/AppContextProvider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
	getCaptchaSolution,
	useCaptchaSetup,
	useSkipPolicyUpload
} from '@/features/Policy/hooks/useSkipPolicyUpload';
import { isFeatureActive } from '@/lib/featureFlags';
import apiService from '@/services/apiService';
import { notifyParent } from '@/services/parentMessenger';
import { FeatureFlag } from '@/types/app';

interface CaptchaSolution {
	[key: string]: unknown;
}

declare global {
	interface Window {
		KROT: {
			setup: (key: string) => void;
			getSolution: () => Promise<CaptchaSolution>;
		};
		filePond: RefObject<FilePond | null> | null;
	}
}

registerPlugin(FilePondPluginFileValidateType, FilePondPluginFileValidateSize);

const PolicyForm = () => {
	const { t } = useTranslation();
	const posthog = usePostHog();
	const { config } = useAppContext();
	const [id, setId] = useState<string | null>(null);
	const [files, setFiles] = useState<FilePondFile[]>([]);
	const [formFeedback, setFormFeedback] = useState<string | null>(null);
	const [isProcessing, setIsProcessing] = useState(false);
	const { isCaptchaReady } = useCaptchaSetup();
	const skipUpload = useSkipPolicyUpload(() => {
		setFormFeedback(t('errors.submitFailed'));
	});
	const filePondRef = useRef<FilePond | null>(null);
	window.filePond = filePondRef;

	const handleProcessFile = async (
		_fieldName: string,
		file: ActualFileObject,
		_metadata: Record<string, unknown>,
		load: (uniqueFileId: string) => void,
		error: (errorMessage: string) => void
	) => {
		try {
			setIsProcessing(true);

			const captchaSolution = await getCaptchaSolution();

			const response = await apiService.policy.upload(
				file as File,
				captchaSolution,
				window.location.href
			);

			if (response.policy_upload_form_id && response.access_token) {
				setId(response.policy_upload_form_id);
				localStorage.setItem(response.policy_upload_form_id, response.access_token);

				posthog?.identify(response.policy_upload_form_id);

				// Lead-tracking: a policy document was successfully uploaded.
				// No-op when not iframed or no parent origin was captured.
				notifyParent('upload');

				load(response.policy_upload_form_id);
				setFormFeedback(null);
			} else {
				throw new Error('No policy upload form ID received');
			}
		} catch (err) {
			console.error('Upload error:', err);
			setFormFeedback(t('errors.uploadFailed'));
			setIsProcessing(false);
			error(t('errors.uploadFailed'));
		}
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		setIsProcessing(true);
		if (!id) {
			setFormFeedback(t('errors.noValidFormId'));
			setIsProcessing(false);
			return;
		}
		try {
			const response = await apiService.policy.submit(id);
			if (response?.temp_redirect_url) {
				window.location.href = response.temp_redirect_url;
			} else {
				throw new Error('Failed to submit policy form');
			}
		} catch (err) {
			console.error('Submit policy form error:', err);
			setFormFeedback(t('errors.submitFailed'));
			setIsProcessing(false);
		}
	};

	const allowNoPolicyFlow = isFeatureActive(config, FeatureFlag.ALLOW_NO_POLICY_FLOW);

	const handleSkipUpload = async () => {
		setFormFeedback(null);
		await skipUpload.skip();
	};

	if (!isCaptchaReady) {
		return <div>{t('common.loadingSecurityCheck')}</div>;
	}

	return (
		<form onSubmit={handleSubmit} data-testid="policy-upload-form">
			<FilePond
				ref={filePondRef}
				{...(filePondConfig as FilePondProps)}
				labelIdle={`${t('policy.upload.dropzone.label')} <span class="filepond--label-action">${t('policy.upload.dropzone.browse')}</span>`}
				labelMaxFileSize={t('policy.upload.dropzone.maxSize')}
				labelFileTypeNotAllowed={t('policy.upload.dropzone.typeNotAllowed')}
				labelFileWaitingForSize={t('policy.upload.dropzone.waitingForSize')}
				labelFileSizeNotAvailable={t('policy.upload.dropzone.sizeNotAvailable')}
				labelFileLoading={t('policy.upload.dropzone.loading')}
				labelFileLoadError={t('policy.upload.dropzone.loadError')}
				labelFileProcessing={t('policy.upload.dropzone.processing')}
				labelFileProcessingComplete={t('policy.upload.dropzone.processingComplete')}
				labelFileProcessingAborted={t('policy.upload.dropzone.processingAborted')}
				labelFileProcessingError={t('policy.upload.dropzone.processingError')}
				labelFileProcessingRevertError={t('policy.upload.dropzone.revertError')}
				labelFileRemoveError={t('policy.upload.dropzone.removeError')}
				labelTapToCancel={t('policy.upload.dropzone.tapToCancel')}
				labelTapToRetry={t('policy.upload.dropzone.tapToRetry')}
				labelTapToUndo={t('policy.upload.dropzone.tapToUndo')}
				onupdatefiles={setFiles}
				onprocessfiles={() => setIsProcessing(false)}
				onremovefile={() => setIsProcessing(false)}
				onaddfile={(error: FilePondErrorDescription | null, _file: FilePondFile) => {
					if (!error) {
						setIsProcessing(true);
					}
				}}
				server={{
					process: handleProcessFile,
					revert: null
				}}
				data-testid="policy-file-upload"
			/>
			{formFeedback && (
				<Alert variant="destructive" className="mt-4 text-sm" data-testid="policy-upload-error">
					<AlertDescription>{formFeedback}</AlertDescription>
				</Alert>
			)}
			<Button
				type="submit"
				size="lg"
				className="w-full mt-2 text-lg h-12 font-base"
				disabled={isProcessing || files.length === 0}
				loading={isProcessing}
				loadingText={isProcessing ? t('policy.upload.processing') : undefined}
				iconRight={!isProcessing ? <ArrowRight className="h-4 w-4" /> : undefined}
				data-testid="policy-upload-button"
			>
				{t('policy.upload.button')}
			</Button>
			{allowNoPolicyFlow && (
				<button
					type="button"
					className="w-full mt-4 text-sm text-muted-foreground hover:text-primary underline underline-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
					onClick={handleSkipUpload}
					disabled={isProcessing || skipUpload.isProcessing}
					data-testid="skip-upload-button"
				>
					{t('policy.upload.skipUpload')}
				</button>
			)}
		</form>
	);
};

export default PolicyForm;
